import { supabase } from '@/integrations/supabase/client';
import type {
  Campaign,
  CampaignInsert,
  CampaignOption,
  CampaignPerformanceRankingRow,
  CampaignRow,
  CampaignUpdate,
  CampaignWithContact,
} from '../types/campaign.types';
import { mapDbRowToCampaign, mapToCampaignOption, type RawCampaignWithJoins } from '../services/campaign.mappers';

export const campaignRepository = {
  /**
   * Fetch all campaigns for the organization with joined brand & contact data
   */
  async getAll(orgId?: string, fallbackBrandName?: string): Promise<Campaign[]> {
    if (orgId) {
      await supabase.rpc('set_current_organization', { org_id: orgId });
    }

    // Query brands for full lookup map
    const { data: brandsData } = await supabase
      .from('brands')
      .select('id, name, logo_url, organization_id');

    const brandMap: Record<string, { id: string; name: string; logo_url?: string | null }> = {};
    (brandsData || []).forEach((b) => {
      brandMap[b.id] = b;
      if (b.organization_id && !brandMap[`org_${b.organization_id}`]) {
        brandMap[`org_${b.organization_id}`] = b;
      }
      if (b.name) {
        brandMap[b.name.toLowerCase()] = b;
      }
    });

    // Query users table for created_by lookup
    const { data: usersData } = await supabase
      .from('users')
      .select('id, auth_user_id, name');

    const userMap: Record<string, string> = {};
    (usersData || []).forEach((u) => {
      if (u.name) {
        if (u.auth_user_id) userMap[u.auth_user_id] = u.name;
        if (u.id) userMap[u.id] = u.name;
      }
    });

    let query = supabase
      .from('campaigns')
      .select(`
        *,
        brands (
          id,
          name,
          logo_url
        ),
        contacts!campaigns_linked_contact_id_fkey (
          id,
          name,
          email
        )
      `)
      .order('created_at', { ascending: false });

    if (orgId) {
      query = query.eq('organization_id', orgId);
    }

    const { data, error } = await query;
    if (error) {
      console.error('Error fetching campaigns:', error);
      throw error;
    }

    return (data || []).map((row) =>
      mapDbRowToCampaign(row as unknown as RawCampaignWithJoins, brandMap, userMap, fallbackBrandName)
    );
  },

  /**
   * Fetch a single campaign with contact and brand info
   */
  async getById(campaignId: string, orgId?: string): Promise<CampaignWithContact | null> {
    if (orgId) {
      await supabase.rpc('set_current_organization', { org_id: orgId });
    }

    let query = supabase
      .from('campaigns')
      .select(`
        *,
        brands (
          id,
          name,
          industry,
          logo_url
        ),
        contacts!campaigns_linked_contact_id_fkey (
          id,
          name,
          email,
          phone_number,
          company_name,
          designation,
          contact_type
        )
      `)
      .eq('id', campaignId);

    if (orgId) {
      query = query.eq('organization_id', orgId);
    }

    const { data, error } = await query.maybeSingle();
    if (error) {
      console.error('Error fetching campaign with contact:', error);
      throw error;
    }

    if (!data) return null;

    let contactData = Array.isArray(data.contacts) ? data.contacts[0] || null : data.contacts || null;

    if (!contactData && data.created_by) {
      const { data: creatorUser } = await supabase
        .from('users')
        .select('id, name, email')
        .or(`auth_user_id.eq.${data.created_by},id.eq.${data.created_by}`)
        .maybeSingle();

      if (creatorUser) {
        contactData = {
          id: creatorUser.id,
          name: creatorUser.name,
          email: creatorUser.email,
        };
      }
    }

    return {
      ...(data as unknown as CampaignRow),
      brands: Array.isArray(data.brands) ? data.brands[0] || null : data.brands || null,
      contact: contactData,
    };
  },

  /**
   * Fetch lightweight campaign options for cross-domain pickers
   */
  async getOptions(orgId?: string): Promise<CampaignOption[]> {
    if (orgId) {
      await supabase.rpc('set_current_organization', { org_id: orgId });
    }

    let query = supabase
      .from('campaigns')
      .select('id, name, status, brands(name)')
      .order('name', { ascending: true });

    if (orgId) {
      query = query.eq('organization_id', orgId);
    }

    const { data, error } = await query;
    if (error) {
      console.error('Error fetching campaign options:', error);
      throw error;
    }

    return (data || []).map((row: any) => {
      const brandName = Array.isArray(row.brands) ? row.brands[0]?.name : row.brands?.name;
      return mapToCampaignOption({
        id: row.id,
        name: row.name,
        brand_name: brandName,
        status: row.status,
      });
    });
  },

  /**
   * Update campaign by ID
   */
  async update(campaignId: string, updates: Partial<CampaignUpdate>, orgId?: string): Promise<CampaignRow> {
    if (orgId) {
      await supabase.rpc('set_current_organization', { org_id: orgId });
    }

    let query = supabase
      .from('campaigns')
      .update(updates)
      .eq('id', campaignId);

    if (orgId) {
      query = query.eq('organization_id', orgId);
    }

    const { data, error } = await query.select().single();
    if (error) {
      console.error('Error updating campaign:', error);
      throw error;
    }

    return data as CampaignRow;
  },

  /**
   * Fetch influencers joined to a campaign
   */
  async getCampaignInfluencers(campaignId: string, orgId?: string) {
    if (orgId) {
      await supabase.rpc('set_current_organization', { org_id: orgId });
    }

    const { data, error } = await supabase
      .from('campaign_influencers')
      .select(`
        *,
        influencers (
          id,
          name,
          handle,
          platform,
          email,
          profile_pic_url,
          followers_count,
          video_view_count,
          engagement_rate
        )
      `)
      .eq('campaign_id', campaignId);

    if (error) {
      console.error('Error fetching campaign influencers:', error);
      throw error;
    }

    return data || [];
  },

  /**
   * Add influencers to a campaign
   */
  async addInfluencersToCampaign(campaignId: string, influencerIds: string[], orgId?: string) {
    if (orgId) {
      await supabase.rpc('set_current_organization', { org_id: orgId });
    }

    const records = influencerIds.map((influencerId) => ({
      campaign_id: campaignId,
      influencer_id: influencerId,
      status: 'contacted' as const,
    }));

    const { data, error } = await supabase
      .from('campaign_influencers')
      .insert(records);

    if (error) {
      console.error('Error adding influencers to campaign:', error);
      throw error;
    }

    return data;
  },

  /**
   * Fetch campaign performance rankings from single consolidated RPC (replaces N+1)
   * Includes resilient fallback if the RPC has not been migrated or returns an error.
   */
  async getRankings(orgId?: string): Promise<CampaignPerformanceRankingRow[]> {
    if (orgId) {
      await supabase.rpc('set_current_organization', { org_id: orgId });
    }

    // Attempt 1: Call single consolidated RPC with p_org_id
    try {
      const { data, error } = await supabase.rpc('get_all_campaign_rankings', {
        p_org_id: orgId || null,
      });

      if (!error && data) {
        return data as CampaignPerformanceRankingRow[];
      }
    } catch {
      // Ignore and proceed to fallback
    }

    // Fallback: If RPC is not yet deployed or errors, fetch campaigns directly
    if (!orgId) return [];

    const { data: campaignsData, error: campaignsError } = await supabase
      .from('campaigns')
      .select('id, name, total_budget, spent_budget, status, start_date, end_date, created_at')
      .eq('organization_id', orgId)
      .is('deleted_at', null);

    if (campaignsError) {
      console.error('Error fetching campaigns fallback for rankings:', campaignsError);
      throw campaignsError;
    }

    if (!campaignsData || campaignsData.length === 0) return [];

    // Fetch deliverable aggregates for each campaign
    const aggregatesPromises = campaignsData.map(async (c) => {
      const { data } = await supabase.rpc('get_deliverable_aggregates', {
        p_campaign_id: c.id,
      });
      return { campaignId: c.id, aggregates: (data || []) as any[] };
    });
    const aggregatesResults = await Promise.all(aggregatesPromises);

    return campaignsData.map((c) => {
      const aggResult = aggregatesResults.find((a) => a.campaignId === c.id);
      const aggregates = aggResult?.aggregates || [];

      let totalViews = 0;
      let totalEngagement = 0;
      aggregates.forEach((agg) => {
        totalViews += Number(agg.total_views || 0);
        totalEngagement += Number(agg.total_engagement || 0);
      });

      return {
        id: c.id,
        name: c.name,
        total_budget: c.total_budget,
        spent_budget: c.spent_budget,
        status: c.status,
        start_date: c.start_date,
        end_date: c.end_date,
        created_at: c.created_at,
        total_views: totalViews,
        total_engagement: totalEngagement,
      };
    });
  },
};

